import { useEffect, useRef, useState } from 'react'
import { useIsFetching } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { SettingsHero } from '@/components/settings/SettingsHero'
import { YouPanel } from '@/components/settings/YouPanel'
import { HouseholdPanel } from '@/components/settings/HouseholdPanel'
import { PlanPanel } from '@/components/settings/PlanPanel'
import { DataPanel } from '@/components/settings/DataPanel'
import { DangerZonePanel } from '@/components/settings/DangerZonePanel'
import type { SettingsSectionId } from '@/components/settings/SettingsPanel'
import { cn } from '@/lib/utils'

const SECTIONS: { id: SettingsSectionId; label: string }[] = [
  { id: 'you', label: 'You' },
  { id: 'household', label: 'Household' },
  { id: 'plan', label: 'Plan & billing' },
  { id: 'data', label: 'Your data' },
  { id: 'danger', label: 'Danger zone' },
]

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export default function SettingsPage() {
  const { hash, key } = useLocation()
  const [current, setCurrent] = useState<SettingsSectionId>('you')

  // Links from elsewhere (banners, onboarding, old /settings/* bookmarks) arrive as #section.
  // Panels grow as their data loads, so wait for the first load to settle, then scroll once.
  const idle = useIsFetching() === 0
  const scrolledFor = useRef<string | null>(null)
  useEffect(() => {
    const target = hash.slice(1)
    if (!target || !idle || scrolledFor.current === key) return
    scrolledFor.current = key
    const frame = requestAnimationFrame(() =>
      scrollToSection(document.getElementById(target) ? target : 'household')
    )
    return () => cancelAnimationFrame(frame)
  }, [hash, key, idle])

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        const top = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
        if (top) setCurrent(top.target.id as SettingsSectionId)
      },
      { rootMargin: '0px 0px -60% 0px' }
    )
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [])

  return (
    <div className="flex flex-col gap-3 p-4 md:gap-5 md:p-10">
      <h2 className="font-serif text-xl font-semibold text-[var(--fl-heading)] md:text-[40px] md:font-bold md:leading-tight">
        Settings
      </h2>

      <SettingsHero />

      <nav
        aria-label="Jump to"
        className="-mx-4 flex gap-1.5 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:px-0"
      >
        {SECTIONS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            aria-current={current === id ? 'true' : undefined}
            onClick={() => {
              setCurrent(id)
              scrollToSection(id)
            }}
            className={cn(
              'flex h-8 shrink-0 items-center rounded-full border px-3 text-[13px] font-medium',
              current === id
                ? 'border-[var(--fl-hero-border)] bg-[var(--fl-hero-bg)] text-[var(--fl-hero-fg)]'
                : 'border-[var(--fl-border)] bg-[var(--fl-surface)] text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]',
              id === 'danger' && current !== id && 'text-[var(--fl-error)]'
            )}
          >
            {label}
          </button>
        ))}
      </nav>

      {/* Desktop: two independent columns so a short panel doesn't leave a gap beside a tall one;
          phone: one column in chip order (the column wrappers dissolve via `contents`). */}
      <div className="flex flex-col gap-3 md:gap-5 lg:grid lg:grid-cols-2 lg:items-start">
        <div className="contents lg:flex lg:flex-col lg:gap-5">
          <div className="order-1">
            <YouPanel />
          </div>
          <div className="order-3">
            <PlanPanel />
          </div>
        </div>
        <div className="contents lg:flex lg:flex-col lg:gap-5">
          <div className="order-2">
            <HouseholdPanel />
          </div>
          <div className="order-4">
            <DataPanel />
          </div>
        </div>
        <div className="order-5 lg:col-span-2">
          <DangerZonePanel />
        </div>
      </div>
    </div>
  )
}
